import React, { useEffect, useState } from 'react';
import { dashboardApi, hotelsApi, ownerPickupsApi, shiftsApi, paymentsApi, cashTransfersApi, companyDuesApi } from '../../services/api';
// TODO(company-dues): restore when utils/receiptPdf.js exists —
// import { openDueReceipt } from '../../utils/receiptPdf';
import { fmt, PAYMENT_TYPES } from '../../utils/billing';
import { useAuth } from '../../context/AuthContext';
import { useConfirm, useNotify } from '../../context/ConfirmContext';
import GuestTasksWidget from '../dashboard/GuestTasksWidget';

const OWNER_OPTIONS = ['Nagaraju', 'Govind'];

const EMPTY_SHIFT_STATS = {
  userCash: 0,
  userUpi: 0,
  userCard: 0,
  userOnline: 0,
  userCashExpenses: 0,
  userOwnerPickups: 0,
  userTotal: 0,
};

const EMPTY_LIVE_DRAWER_STATS = {
  cash: 0,
  online: 0,
  total: 0,
  checkouts: 0,
  ownerPickupsToday: 0,
};

const EMPTY_MONTH_SUMMARY = {
  totalCollection: 0,
  cashCollection: 0,
  onlineCollection: 0,
  expenses: 0,
  moneyGivenOut: 0,
  operatingProfit: 0,
  postWithdrawalBalance: 0,
};

function roundMoney(value) {
  return Math.round(Number(value || 0) * 100) / 100;
}

function hasMoneyMismatch(value) {
  return Math.abs(roundMoney(value)) >= 0.01;
}

function getCurrentMonthValue() {
  return new Date().toISOString().slice(0, 7);
}

function getMonthRange(monthValue) {
  const [year, month] = (monthValue || getCurrentMonthValue()).split('-').map(Number);
  const start = `${year}-${String(month).padStart(2, '0')}-01`;
  const end = new Date(year, month, 0).toISOString().slice(0, 10);
  const label = new Date(year, month - 1, 1).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
  });
  return { start, end, label };
}

function summarizeMonthlyFinancials(results) {
  const recipientMap = {};
  const records = [];
  const hotelBreakdowns = results.map((item) => {
    const cash = Number(item.dashboard?.todayPay?.cash) || 0;
    const upi = Number(item.dashboard?.todayPay?.upi) || 0;
    const card = Number(item.dashboard?.todayPay?.card) || 0;
    const total = Number(item.dashboard?.todayPay?.total) || 0;
    const expenses = Number(item.dashboard?.expenses?.today_exp) || 0;
    const recipientTotalsMap = {};
    const moneyGiven = (item.pickups || []).reduce((sum, row) => {
      const amount = Number(row.amount) || 0;
      const recipientLabel = row.recipient_type === 'owner'
        ? row.taken_by
        : 'Other / Purpose';

      records.push({ ...row, hotelName: item.hotel.name, hotelCity: item.hotel.city, hotelColor: item.hotel.color });
      recipientMap[recipientLabel] = (recipientMap[recipientLabel] || 0) + amount;
      recipientTotalsMap[recipientLabel] = (recipientTotalsMap[recipientLabel] || 0) + amount;
      return sum + amount;
    }, 0); // ← initial value 0 prevents NaN when pickups array is empty

    const operatingProfit = total - expenses;
    const postWithdrawalBalance = Math.max(0, operatingProfit - moneyGiven);

    return {
      hotelId: item.hotel.id,
      hotelName: item.hotel.name,
      hotelCity: item.hotel.city,
      hotelColor: item.hotel.color,
      totalCollection: total,
      cashCollection: cash,
      onlineCollection: upi + card,
      expenses,
      moneyGivenOut: moneyGiven,
      operatingProfit,
      postWithdrawalBalance,
      recordCount: (item.pickups || []).length,
      recipientTotals: Object.entries(recipientTotalsMap)
        .map(([name, totalValue]) => ({ name, total: totalValue }))
        .sort((a, b) => b.total - a.total),
    };
  });

  const summary = hotelBreakdowns.reduce((acc, item) => {
    acc.totalCollection += item.totalCollection;
    acc.cashCollection += item.cashCollection;
    acc.onlineCollection += item.onlineCollection;
    acc.expenses += item.expenses;
    acc.moneyGivenOut += item.moneyGivenOut;
    return acc;
  }, { ...EMPTY_MONTH_SUMMARY });

  summary.operatingProfit = summary.totalCollection - summary.expenses;
  summary.postWithdrawalBalance = Math.max(0, summary.operatingProfit - summary.moneyGivenOut);

  const recipientTotals = Object.entries(recipientMap)
    .map(([name, total]) => ({ name, total }))
    .sort((a, b) => b.total - a.total);

  records.sort((a, b) => new Date(b.taken_at || b.created_at) - new Date(a.taken_at || a.created_at));
  hotelBreakdowns.sort((a, b) => {
    if (b.moneyGivenOut !== a.moneyGivenOut) return b.moneyGivenOut - a.moneyGivenOut;
    return a.hotelName.localeCompare(b.hotelName);
  });

  return { summary, recipientTotals, records, hotelBreakdowns };
}

function getRecordRecipient(form) {
  return form.recipientType === 'owner' ? form.ownerName : form.otherName.trim();
}

function getRecordDetails(form) {
  return form.details.trim();
}

function getInitialRecordForm(hotelId) {
  return {
    hotelId: hotelId || '',
    recipientType: 'owner',
    ownerName: OWNER_OPTIONS[0],
    otherName: '',
    purpose: '',
    details: '',
    amount: '',
  };
}

export default function ShiftPage({ hotel, onSuccess }) {
  const notify = useNotify();
  const confirm = useConfirm();
  const { user } = useAuth();
  const isAdmin = user?.role === 'superadmin' || user?.role === 'manager';
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthValue());
  const [availableHotels, setAvailableHotels] = useState([]);
  const [monthlySummary, setMonthlySummary] = useState(EMPTY_MONTH_SUMMARY);
  const [recipientTotals, setRecipientTotals] = useState([]);
  const [moneyRecords, setMoneyRecords] = useState([]);
  const [hotelBreakdowns, setHotelBreakdowns] = useState([]);
  const [recordModalOpen, setRecordModalOpen] = useState(false);
  const [recordForm, setRecordForm] = useState(getInitialRecordForm(''));
  const [recordPreviewCash, setRecordPreviewCash] = useState(0);
  const [transferForm, setTransferForm] = useState(null); // { hotelId, amount, last5, guestName, note } while the modal is open
  const [transferDrawerCash, setTransferDrawerCash] = useState(null);
  const [transferSaving, setTransferSaving] = useState(false);
  const [companyDues, setCompanyDues] = useState([]);
  const [collectingDue, setCollectingDue] = useState(null); // { due, rows: [{id,type,amount,last5}] }
  const [collectSaving, setCollectSaving] = useState(false);

  const [activeShift, setActiveShift] = useState(null);
  const [shiftStats, setShiftStats] = useState(EMPTY_SHIFT_STATS);
  const [cashInHand, setCashInHand] = useState('');
  const [openingRemark, setOpeningRemark] = useState('');
  const [actualCash, setActualCash] = useState('');
  const [tallyRemark, setTallyRemark] = useState('');
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [resultModal, setResultModal] = useState(null);
  const [drawerExpectedCash, setDrawerExpectedCash] = useState(0);
  const [liveDrawerStats, setLiveDrawerStats] = useState(EMPTY_LIVE_DRAWER_STATS);

  const [transactions, setTransactions] = useState([]);
  const [txSearch, setTxSearch] = useState('');
  const [txCategoryFilter, setTxCategoryFilter] = useState('all');
  const [txMethodFilter, setTxMethodFilter] = useState('all');
  const [editingTx, setEditingTx] = useState(null); // { id, type, amount, last5, note }
  const [txDate, setTxDate] = useState('');
  const [pendingOwnerCredits, setPendingOwnerCredits] = useState([]);
  const [settlingCredit, setSettlingCredit] = useState(null); // { id, amount, guest_name, room, settledAmount }

  const monthRange = getMonthRange(selectedMonth);
  const recordAmount = Number(recordForm.amount || 0);
  const recordCashAfter = Math.max(0, Number(recordPreviewCash || 0) - recordAmount);
  const openingDifference = cashInHand === '' ? null : roundMoney(Number(cashInHand) - Number(drawerExpectedCash || 0));

  useEffect(() => {
    if (hotel && user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel?.id, user?.id, selectedMonth]);

  const loadPendingOwnerCredits = () => {
    if (!hotel?.id) return;
    paymentsApi.pendingOwnerCredits(hotel.id).then(setPendingOwnerCredits).catch(() => setPendingOwnerCredits([]));
  };
  useEffect(() => {
    loadPendingOwnerCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel?.id]);

  useEffect(() => {
    if (!recordModalOpen) return;

    const targetHotelId = recordForm.hotelId;
    if (!targetHotelId) {
      setRecordPreviewCash(0);
      return;
    }

    let ignore = false;
    shiftsApi.getStats(targetHotelId)
      .then((data) => {
        if (!ignore) setRecordPreviewCash(Number(data.expectedCash || 0));
      })
      .catch(() => {
        if (!ignore) setRecordPreviewCash(0);
      });

    return () => { ignore = true; };
  }, [recordModalOpen, recordForm.hotelId]);

  useEffect(() => {
    const handleTrigger = () => {
      if (!isAdmin && activeShift && !confirmModalOpen && !resultModal) {
        setActualCash('');
        setTallyRemark('');
        setConfirmModalOpen(true);
      }
    };

    window.addEventListener('trigger-shift-handover', handleTrigger);
    return () => window.removeEventListener('trigger-shift-handover', handleTrigger);
  }, [isAdmin, activeShift, confirmModalOpen, resultModal]);

  useEffect(() => {
    if (confirmModalOpen && hotel?.id) {
      shiftsApi.getStats(hotel.id).then(liveStats => {
        setLiveDrawerStats({
          cash: Number(liveStats.todayCash || 0),
          online: Number(liveStats.todayOnline || 0),
          total: Number(liveStats.todayTotal || 0),
          checkouts: Number(liveStats.checkouts || 0),
          todayExpenses: Number(liveStats.todayCashExpenses || 0),
          ownerPickupsToday: Number(liveStats.ownerPickupsToday || 0),
        });
      }).catch(() => {});
      if (activeShift) {
        shiftsApi.getStats(hotel.id, activeShift.start_at, activeShift.user_id).then(mineStats => {
          setShiftStats({
            userCash: Number(mineStats.userCash || 0),
            userUpi: Number(mineStats.userUpi || 0),
            userCard: Number(mineStats.userCard || 0),
            userOnline: Number(mineStats.userOnline || 0),
            userCashExpenses: Number(mineStats.userCashExpenses || 0),
            userOwnerPickups: Number(mineStats.userOwnerPickups || 0),
            userTotal: Number(mineStats.userTotal || 0),
          });
        }).catch(() => {});
      }
    }
  }, [confirmModalOpen, hotel?.id, activeShift]);

  async function load() {
    setLoading(true);
    try {
      const adminHotels = isAdmin ? await hotelsApi.list().catch(() => []) : [];
      if (isAdmin) setAvailableHotels(adminHotels);

      const scopeHotels = isAdmin
        ? (hotel?.id === 'all' ? adminHotels : adminHotels.filter(h => String(h.id) === String(hotel?.id)))
        : (hotel ? [hotel] : []);

      const monthlyResults = await Promise.all(scopeHotels.map(async (property) => {
        const [dashboard, pickups] = await Promise.all([
          dashboardApi.stats(property.id, monthRange.start, monthRange.end).catch(() => ({})),
          ownerPickupsApi.list(property.id, { start: monthRange.start, end: monthRange.end }).catch(() => []),
        ]);
        return { hotel: property, dashboard, pickups };
      }));

      const { summary, recipientTotals: recipientBreakdown, records, hotelBreakdowns: hotelFinancials } = summarizeMonthlyFinancials(monthlyResults);
      setMonthlySummary(summary);
      setRecipientTotals(recipientBreakdown);
      setMoneyRecords(records);
      setHotelBreakdowns(hotelFinancials);

      const targetHId = hotel?.id && hotel.id !== 'all' ? hotel.id : (adminHotels[0]?.id || '');
      if (targetHId) {
        const txs = await shiftsApi.transactions(targetHId, { date: txDate || undefined }).catch(() => []);
        setTransactions(txs);
        setCompanyDues(await companyDuesApi.list(targetHId).catch(() => []));
      }

      if (!isAdmin && hotel?.id) {
        const [allShifts, liveStats] = await Promise.all([
          shiftsApi.list(hotel.id),
          shiftsApi.getStats(hotel.id),
        ]);

        const mine = allShifts.find(s => s.status === 'active' && String(s.user_id) === String(user.id));
        setActiveShift(mine || null);
        setDrawerExpectedCash(Number(liveStats.expectedCash || 0));
        setLiveDrawerStats({
          cash: Number(liveStats.todayCash || 0),
          online: Number(liveStats.todayOnline || 0),
          total: Number(liveStats.todayTotal || 0),
          checkouts: Number(liveStats.checkouts || 0),
          todayExpenses: Number(liveStats.todayCashExpenses || 0),
          ownerPickupsToday: Number(liveStats.ownerPickupsToday || 0),
        });

        if (mine) {
          const mineStats = await shiftsApi.getStats(hotel.id, mine.start_at, mine.user_id);
          setShiftStats({
            userCash: Number(mineStats.userCash || 0),
            userUpi: Number(mineStats.userUpi || 0),
            userCard: Number(mineStats.userCard || 0),
            userOnline: Number(mineStats.userOnline || 0),
            userCashExpenses: Number(mineStats.userCashExpenses || 0),
            userOwnerPickups: Number(mineStats.userOwnerPickups || 0),
            userTotal: Number(mineStats.userTotal || 0),
          });
        } else {
          setShiftStats(EMPTY_SHIFT_STATS);
        }
      }
    } catch (err) {
      console.error('[CashManagement] load error', err);
    } finally {
      setLoading(false);
    }
  }

  function openRecordModal() {
    const defaultHotelId = hotel?.id && hotel.id !== 'all'
      ? hotel.id
      : (availableHotels[0]?.id || '');
    setRecordForm(getInitialRecordForm(defaultHotelId));
    setRecordPreviewCash(0);
    setRecordModalOpen(true);
  }

  async function submitRecord() {
    const recipientName = getRecordRecipient(recordForm);
    const details = getRecordDetails(recordForm);

    if (!recordForm.hotelId) {
      await notify('Please choose the hotel for this entry.');
      return;
    }
    if (!recordForm.amount || Number(recordForm.amount) <= 0) {
      await notify('Please enter a valid amount.');
      return;
    }
    if (recordForm.recipientType === 'other' && !recordForm.purpose.trim()) {
      await notify('Please enter the purpose.');
      return;
    }
    if (!recipientName) {
      await notify('Please enter who received the money.');
      return;
    }
    if (recordForm.recipientType === 'other' && !details) {
      await notify('Please enter the details for this payment.');
      return;
    }

    setLoading(true);
    try {
      await ownerPickupsApi.create({
        hotelId: recordForm.hotelId,
        recipientType: recordForm.recipientType,
        takenBy: recipientName,
        purpose: recordForm.recipientType === 'other' ? recordForm.purpose.trim() : null,
        note: details || null,
        amount: Number(recordForm.amount),
        shiftId: activeShift?.id || null,
      });
      setRecordModalOpen(false);
      await load();
      onSuccess?.('Money given entry recorded.');
    } catch (err) {
      await notify(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleJoinShift() {
    const opening = Number(cashInHand);
    if (cashInHand === '' || Number.isNaN(opening)) return;
    if (openingDifference !== null && hasMoneyMismatch(openingDifference) && !String(openingRemark || '').trim()) {
      await notify('Please add an opening note before starting the shift.');
      return;
    }
    setLoading(true);
    try {
      await shiftsApi.create({ hotelId: hotel.id, openingCash: opening, openingRemark: openingRemark || null });
      setCashInHand('');
      setOpeningRemark('');
      await load();
      onSuccess?.('Shift started.');
    } catch (err) {
      await notify(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleEndShift() {
    if (!activeShift) return;
    setLoading(true);
    try {
      // Re-fetch fresh drawer snapshot so the difference is accurate
      // even if checkouts or pickups happened since the page loaded
      let freshExpected = drawerExpectedCash;
      try {
        const freshStats = await shiftsApi.getStats(hotel.id);
        freshExpected = Number(freshStats.expectedCash || 0);
        setDrawerExpectedCash(freshExpected);
      } catch (_) {
        // fall back to state value if the refresh fails
      }

      const difference = roundMoney(Number(actualCash) - freshExpected);
      await shiftsApi.update(activeShift.id, {
        closingCash: Number(actualCash),
        collectedCash: shiftStats.userCash,
        collectedUpi:  shiftStats.userUpi  || 0,
        collectedCard: shiftStats.userCard || 0,
        totalCollected: shiftStats.userTotal,
        cashDifference: difference,
        tallyRemark: tallyRemark || null,
        status: 'completed',
      });
      setResultModal({
        closingCash: Number(actualCash),
        difference,
      });
      setConfirmModalOpen(false);
      setActiveShift(null);
      setShiftStats(EMPTY_SHIFT_STATS);
      await load();
    } catch (err) {
      await notify(err.message);
    } finally {
      setLoading(false);
    }
  }

  function renderOverviewStrip() {
    return (
      <div className="panel" style={{ padding: 22, borderRadius: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
              {monthRange.label} Snapshot
            </div>
            <div style={{ fontSize: 24, fontWeight: 900, marginTop: 8 }}>
              {hotel?.id === 'all' ? 'All Hotels Combined' : `${hotel?.name} Financials`}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>
              Collections and money-given entries are kept separate so owners can quickly review cash flow.
            </div>
          </div>
          {hotel?.id === 'all' && (
            <div style={{ background: 'var(--s1)', borderRadius: 16, padding: '12px 16px', minWidth: 180 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase' }}>Hotels Covered</div>
              <div style={{ fontSize: 28, fontWeight: 900, marginTop: 8 }}>{hotelBreakdowns.length}</div>
            </div>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14, marginBottom: 14 }}>
          {[
            { label: 'Total Collection', value: fmt(monthlySummary.totalCollection), color: '#111827' },
            { label: 'Cash', value: fmt(monthlySummary.cashCollection), color: 'var(--success)' },
            { label: 'UPI / Card', value: fmt(monthlySummary.onlineCollection), color: 'var(--teal)' },
            { label: 'Expenses', value: fmt(monthlySummary.expenses), color: 'var(--danger)' },
            { label: 'Money Given', value: fmt(monthlySummary.moneyGivenOut), color: '#b45309' },
          ].map((item) => (
            <div key={item.label} style={{ padding: 16, borderRadius: 18, background: 'var(--s1)', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase' }}>{item.label}</div>
              <div style={{ fontSize: 28, fontWeight: 900, marginTop: 10, color: item.color }}>{item.value}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>
          Operating profit: <strong style={{ color: 'var(--text)' }}>{fmt(monthlySummary.operatingProfit)}</strong> ·
          Balance after money given out: <strong style={{ color: 'var(--text)' }}> {fmt(monthlySummary.postWithdrawalBalance)}</strong>
        </div>
      </div>
    );
  }

  function renderRecipientBreakdown() {
    if (recipientTotals.length === 0) return null;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        {recipientTotals.map((item) => (
          <div key={item.name} style={{ background: 'var(--s1)', borderRadius: 999, padding: '8px 12px', fontSize: 12, border: '1px solid var(--border)' }}>
            <strong>{item.name}</strong>: {fmt(item.total)}
          </div>
        ))}
      </div>
    );
  }

  function renderMoneyRegister() {
    const registerActionLabel = isAdmin ? 'Record Money Given' : 'Give Money to Owner';
    return (
      <div className="panel" style={{ borderRadius: 24 }}>
        <div className="panel-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span className="panel-title">🧾 Money Given Register</span>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
              Every cash handover entry for {monthRange.label}
            </div>
          </div>
          <button className="btn btn-danger" onClick={openRecordModal}>{registerActionLabel}</button>
        </div>
        <div style={{ padding: 20 }}>
          {renderRecipientBreakdown()}
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  {hotel?.id === 'all' && <th>Hotel</th>}
                  <th>Given To</th>
                  <th>Type</th>
                  <th>Purpose</th>
                  <th>Details</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th>Recorded By</th>
                </tr>
              </thead>
              <tbody>
                {moneyRecords.length === 0 ? (
                  <tr>
                    <td colSpan={hotel?.id === 'all' ? 8 : 7}>
                      <div className="empty">
                        <div className="empty-icon">🧾</div>
                        No money-given records for {monthRange.label}
                      </div>
                    </td>
                  </tr>
                ) : moneyRecords.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div style={{ fontWeight: 700 }}>{new Date(row.taken_at || row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{new Date(row.taken_at || row.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                    </td>
                    {hotel?.id === 'all' && <td>{row.hotelName}</td>}
                    <td style={{ fontWeight: 700 }}>{row.taken_by}</td>
                    <td>
                      <span className={`badge ${row.recipient_type === 'owner' ? 'b-gold' : 'b-red'}`}>
                        {row.recipient_type === 'owner' ? 'Owner' : 'Other'}
                      </span>
                    </td>
                    <td>{row.purpose || 'Owner collection'}</td>
                    <td style={{ color: 'var(--muted)' }}>{row.note || '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 900, color: '#b45309' }}>{fmt(row.amount)}</td>
                    <td>{row.recorded_by_name || row.recorded_by || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  function renderHotelBreakdowns() {
    if (hotelBreakdowns.length === 0) return null;

    return (
      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Hotel Wise View
          </div>
          <div style={{ fontSize: 26, fontWeight: 900, marginTop: 6 }}>
            {hotel?.id === 'all' ? 'How much was taken from each hotel' : 'This hotel monthly position'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>
            Each card keeps financials separate so owners can understand collections, expenses, and money taken without mixing hotels together.
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18 }}>
          {hotelBreakdowns.map((item) => (
            <div key={item.hotelId} className="panel" style={{ padding: 20, borderRadius: 24, overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 999, background: item.hotelColor || 'var(--gold)' }} />
                    <div style={{ fontSize: 22, fontWeight: 900 }}>{item.hotelName}</div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>{item.hotelCity || 'Hotel financial overview'}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase' }}>Money Given</div>
                  <div style={{ fontSize: 30, fontWeight: 900, color: '#b45309', marginTop: 6 }}>{fmt(item.moneyGivenOut)}</div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                {[
                  { label: 'Total Collection', value: item.totalCollection, color: '#111827' },
                  { label: 'Cash', value: item.cashCollection, color: 'var(--success)' },
                  { label: 'UPI / Card', value: item.onlineCollection, color: 'var(--teal)' },
                  { label: 'Expenses', value: item.expenses, color: 'var(--danger)' },
                  { label: 'Operating Profit', value: item.operatingProfit, color: 'var(--accent)' },
                  { label: 'Balance After Outflow', value: item.postWithdrawalBalance, color: '#7c3aed' },
                ].map((stat) => (
                  <div key={stat.label} style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 16, padding: 14 }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase' }}>{stat.label}</div>
                    <div style={{ fontSize: 22, fontWeight: 900, marginTop: 8, color: stat.color }}>{fmt(stat.value)}</div>
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 8 }}>
                  Who Took Money
                </div>
                {item.recipientTotals.length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--muted)' }}>No money-given entries recorded for {monthRange.label}.</div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {item.recipientTotals.map((person) => (
                      <div key={`${item.hotelId}-${person.name}`} style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 999, padding: '8px 12px', fontSize: 12 }}>
                        <strong>{person.name}</strong>: {fmt(person.total)}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                Entries recorded this month: <strong style={{ color: 'var(--text)' }}>{item.recordCount}</strong>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  function renderStaffPickupHistory() {
    const localToday = new Date().toLocaleDateString('en-CA');
    const shiftStart = activeShift?.start_at ? new Date(activeShift.start_at) : null;

    const todayRecords = moneyRecords.filter(row => {
      const recDate = row.taken_at || row.created_at;
      if (!recDate) return false;
      const d = new Date(recDate);
      const rowLocalDate = d.toLocaleDateString('en-CA');
      const isToday = rowLocalDate === localToday;
      const isThisShift = shiftStart ? d >= shiftStart : false;
      return isToday || isThisShift;
    });

    return (
      <div className="panel" style={{ borderRadius: 22, marginTop: 24, overflow: 'hidden' }}>
        <div style={{ padding: '18px 20px 12px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>🧾 Money Given Today</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>
              Record of all cash handed out during today's shift
            </div>
          </div>
          {todayRecords.length > 0 && (
            <div style={{ background: 'rgba(180,83,9,0.1)', borderRadius: 999, padding: '6px 14px', fontSize: 13, fontWeight: 800, color: '#b45309' }}>
              Total: {fmt(todayRecords.reduce((s, r) => s + Number(r.amount || 0), 0))}
            </div>
          )}
        </div>
        <div style={{ padding: 16 }}>
          {todayRecords.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--muted)', fontSize: 13 }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
              No money handed out today
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {todayRecords.map((row) => (
                <div key={row.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: 'var(--s1)', borderRadius: 14, border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ width: 38, height: 38, borderRadius: 999, background: row.recipient_type === 'owner' ? 'rgba(180,83,9,0.12)' : 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                      {row.recipient_type === 'owner' ? '👤' : '💸'}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{row.taken_by}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                        {row.purpose || 'Owner collection'}{row.note ? ` · ${row.note}` : ''}
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 900, fontSize: 18, color: '#b45309' }}>{fmt(row.amount)}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>
                      {new Date(row.taken_at || row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>
                      {new Date(row.taken_at || row.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── CASH TRANSFER (guest pays UPI, reception hands over cash) ───────────
  function openTransferModal() {
    const hotelId = hotel?.id && hotel.id !== 'all' ? hotel.id : (availableHotels[0]?.id || '');
    setTransferForm({ hotelId, amount: '', last5: '', guestName: '', note: '' });
  }

  useEffect(() => {
    if (!transferForm?.hotelId) { setTransferDrawerCash(null); return; }
    let ignore = false;
    shiftsApi.getStats(transferForm.hotelId)
      .then(data => { if (!ignore) setTransferDrawerCash(Number(data.expectedCash || 0)); })
      .catch(() => { if (!ignore) setTransferDrawerCash(null); });
    return () => { ignore = true; };
  }, [transferForm?.hotelId]);

  async function saveTransfer() {
    const amount = Number(transferForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) { await notify('Enter a valid amount.'); return; }
    if (!String(transferForm.last5 || '').trim()) { await notify('Enter the UPI reference (last 5 digits) from the PhonePe/UPI payment.'); return; }
    if (transferDrawerCash !== null && amount > transferDrawerCash + 0.009) {
      await notify(`Only ${fmt(transferDrawerCash)} cash is expected in the drawer.`);
      return;
    }
    const noteParts = [transferForm.guestName.trim(), transferForm.note.trim()].filter(Boolean);
    setTransferSaving(true);
    try {
      await cashTransfersApi.create({ hotelId: transferForm.hotelId, amount, last5: transferForm.last5, note: noteParts.join(' · ') });
      setTransferForm(null);
      onSuccess?.(`Cash transfer of ${fmt(amount)} recorded — UPI +${fmt(amount)}, drawer cash -${fmt(amount)}.`);
      load();
    } catch (err) { await notify(err.message); }
    setTransferSaving(false);
  }

  function renderTransferModal() {
    if (!transferForm) return null;
    const amount = Number(transferForm.amount) || 0;
    return (
      <div className="modal-overlay" style={{ zIndex: 10000 }}>
        <div className="modal-content" style={{ maxWidth: 520 }}>
          <h2 style={{ marginTop: 0 }}>⇄ Cash Transfer</h2>
          <p style={{ color: 'var(--muted)', marginTop: 6, fontSize: 13 }}>
            Guest pays by PhonePe / UPI and takes the same amount in cash from reception.
            Recorded in cash management only — <strong>not added to any bill</strong>.
          </p>
          {(!hotel?.id || hotel.id === 'all') && (
            <div className="field">
              <label>Hotel *</label>
              <select className="input" value={transferForm.hotelId} onChange={e => setTransferForm(f => ({ ...f, hotelId: e.target.value }))}>
                <option value="">Select Hotel</option>
                {availableHotels.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
              </select>
            </div>
          )}
          <div className="form-row-2">
            <div className="field">
              <label>Amount (₹) *</label>
              <input type="number" min="1" value={transferForm.amount} onChange={e => setTransferForm(f => ({ ...f, amount: e.target.value }))} autoFocus />
            </div>
            <div className="field">
              <label>UPI Reference (Last 5) *</label>
              <input value={transferForm.last5} maxLength={5} placeholder="e.g. 43210" onChange={e => setTransferForm(f => ({ ...f, last5: e.target.value.slice(-5) }))} />
            </div>
          </div>
          <div className="form-row-2">
            <div className="field">
              <label>Guest Name / Room</label>
              <input value={transferForm.guestName} placeholder="Optional" onChange={e => setTransferForm(f => ({ ...f, guestName: e.target.value }))} />
            </div>
            <div className="field">
              <label>Remarks</label>
              <input value={transferForm.note} placeholder="Optional" onChange={e => setTransferForm(f => ({ ...f, note: e.target.value }))} />
            </div>
          </div>
          <div style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', fontSize: 13, display: 'grid', gap: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>📱 UPI received</span><strong style={{ color: 'var(--teal)' }}>+{fmt(amount)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>💵 Cash handed to guest</span><strong style={{ color: 'var(--danger)' }}>-{fmt(amount)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--muted)' }}>
              <span>Drawer cash after</span>
              <span>{transferDrawerCash === null ? '…' : fmt(Math.max(0, transferDrawerCash - amount))}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button className="btn btn-ghost" style={{ flex: 1, border: '1px solid var(--border2)' }} onClick={() => setTransferForm(null)} disabled={transferSaving}>Cancel</button>
            <button className="btn btn-gold" style={{ flex: 1.5 }} onClick={saveTransfer} disabled={transferSaving || !transferForm.hotelId}>
              {transferSaving ? 'Saving…' : 'Record Transfer'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  function renderRecordModal() {
    if (!recordModalOpen) return null;
    const recipientName = getRecordRecipient(recordForm);
    const selectedHotel = availableHotels.find(h => String(h.id) === String(recordForm.hotelId));
    const modalTitle = recordForm.recipientType === 'owner' ? 'Give Money to Owner' : 'Record Cash Handed Out';
    const modalCopy = recordForm.recipientType === 'owner'
      ? 'Use this when staff hands cash to Nagaraju or Govind.'
      : 'Use this when staff hands cash to someone else and purpose/details must be captured.';

    return (
      <div className="modal-overlay" style={{ zIndex: 10000 }}>
        <div className="modal-content" style={{ maxWidth: 560 }}>
          <h2 style={{ marginTop: 0 }}>{modalTitle}</h2>
          <p style={{ color: 'var(--muted)', marginTop: 6 }}>
            {modalCopy}
          </p>

          {hotel?.id === 'all' ? (
            <div className="field" style={{ marginBottom: 14 }}>
              <label>Hotel</label>
              <select value={recordForm.hotelId} onChange={e => setRecordForm(f => ({ ...f, hotelId: e.target.value }))}>
                <option value="">Select Hotel</option>
                {availableHotels.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
              </select>
            </div>
          ) : (
            <div style={{ marginBottom: 14, fontSize: 12, color: 'var(--muted)' }}>
              Property: <strong style={{ color: 'var(--text)' }}>{hotel?.name}</strong>
            </div>
          )}

          {selectedHotel && (
            <div style={{ marginBottom: 14, fontSize: 12, color: 'var(--muted)' }}>
              Property: <strong style={{ color: 'var(--text)' }}>{selectedHotel.name}</strong>
            </div>
          )}

          <div className="field" style={{ marginBottom: 14 }}>
            <label>Recipient Type</label>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className={`btn ${recordForm.recipientType === 'owner' ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setRecordForm(f => ({ ...f, recipientType: 'owner', otherName: '', purpose: '' }))}>Owner</button>
              <button type="button" className={`btn ${recordForm.recipientType === 'other' ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setRecordForm(f => ({ ...f, recipientType: 'other', ownerName: OWNER_OPTIONS[0] }))}>Other</button>
            </div>
          </div>

          {recordForm.recipientType === 'owner' ? (
            <div className="field" style={{ marginBottom: 14 }}>
              <label>Money Given To</label>
              <select value={recordForm.ownerName} onChange={e => setRecordForm(f => ({ ...f, ownerName: e.target.value }))}>
                {OWNER_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
              </select>
            </div>
          ) : (
            <>
              <div className="field" style={{ marginBottom: 14 }}>
                <label>Money Given To</label>
                <input value={recordForm.otherName} onChange={e => setRecordForm(f => ({ ...f, otherName: e.target.value }))} placeholder="Enter person or party name" />
              </div>
              <div className="field" style={{ marginBottom: 14 }}>
                <label>Purpose</label>
                <input value={recordForm.purpose} onChange={e => setRecordForm(f => ({ ...f, purpose: e.target.value }))} placeholder="Why this amount was given" />
              </div>
            </>
          )}

          <div className="field" style={{ marginBottom: 14 }}>
            <label>Amount</label>
            <input type="number" value={recordForm.amount} onChange={e => setRecordForm(f => ({ ...f, amount: e.target.value }))} placeholder="0" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div style={{ background: 'var(--s1)', borderRadius: 12, padding: 12 }}>
              <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase' }}>Current Drawer Cash</div>
              <div style={{ fontWeight: 900, marginTop: 4 }}>{fmt(recordPreviewCash)}</div>
            </div>
            <div style={{ background: 'var(--s1)', borderRadius: 12, padding: 12 }}>
              <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase' }}>Estimated Cash After</div>
              <div style={{ fontWeight: 900, marginTop: 4, color: 'var(--success)' }}>{fmt(recordCashAfter)}</div>
            </div>
          </div>

          <div className="field" style={{ marginBottom: 18 }}>
            <label>Details</label>
            <textarea value={recordForm.details} onChange={e => setRecordForm(f => ({ ...f, details: e.target.value }))} rows={4} placeholder={recordForm.recipientType === 'owner' ? 'Optional details or note' : 'Required details for this payment'} style={{ width: '100%', resize: 'vertical' }} />
          </div>

          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 18 }}>
            Entry preview: <strong style={{ color: 'var(--text)' }}>{recipientName || '—'}</strong> · {fmt(recordAmount)}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <button className="btn btn-ghost" onClick={() => setRecordModalOpen(false)}>Cancel</button>
            <button className="btn btn-danger" disabled={!recordAmount || recordAmount > Number(recordPreviewCash || 0)} onClick={submitRecord}>Save Entry</button>
          </div>
        </div>
      </div>
    );
  }

  function renderConfirmModal() {
    if (!confirmModalOpen) return null;
    // `||` treats a legitimate 0 as "missing" and falls through to the wrong-scoped
    // value — e.g. this shift's own owner-handovers being genuinely 0 (nothing handed
    // over yet THIS shift) fell back to liveDrawerStats.ownerPickupsToday, the WHOLE
    // CALENDAR DAY's pickups across every shift, resurfacing a handover already
    // accounted for in a previous (already-closed) shift today. `??` only falls back
    // on actual null/undefined, letting a real 0 stay 0.
    const openingCash = Number(activeShift?.opening_cash ?? liveDrawerStats?.drawerBaseCash ?? 0);
    const shiftCashCollected = Number(shiftStats?.userCash ?? 0);
    const shiftCashExpenses = Number(shiftStats?.userCashExpenses ?? 0);
    const ownerHandovers = Number(shiftStats?.userOwnerPickups ?? 0);
    const calculatedExpected = roundMoney(openingCash + shiftCashCollected - shiftCashExpenses - ownerHandovers);
    const calcDifference = actualCash === '' ? null : roundMoney(Number(actualCash) - calculatedExpected);

    return (
      <div className="modal-overlay" style={{ zIndex: 9999 }}>
        <div className="modal-content" style={{ maxWidth: 480 }}>
          <h3 style={{ marginTop: 0, fontSize: 20 }}>End Shift — Cash Reconciliation</h3>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: -6, marginBottom: 16 }}>
            Review opening balance and current shift totals before ending shift
          </p>

          <div style={{ background: 'var(--s1)', border: '1px solid var(--border)', padding: '14px 16px', borderRadius: 14, marginBottom: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span>📥 Opening / Carried Forward Cash:</span>
              <strong>{fmt(openingCash)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--success)' }}>
              <span>💰 Current Shift Cash Collected:</span>
              <strong>+{fmt(shiftCashCollected)}</strong>
            </div>
            {shiftCashExpenses > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--danger)' }}>
                <span>💸 Shift Cash Expenses:</span>
                <strong>-{fmt(shiftCashExpenses)}</strong>
              </div>
            )}
            {ownerHandovers > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#b45309' }}>
                <span>👤 Money Handed to Owner:</span>
                <strong>-{fmt(ownerHandovers)}</strong>
              </div>
            )}
            <div style={{ borderTop: '2px dashed var(--border)', paddingTop: 10, marginTop: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 800 }}>💵 Total Expected Cash in Drawer:</span>
              <strong style={{ fontSize: 18, color: 'var(--text)' }}>{fmt(calculatedExpected)}</strong>
            </div>
          </div>

          <div className="field" style={{ marginBottom: 14 }}>
            <label style={{ fontWeight: 700, fontSize: 13 }}>Actual Physical Cash Counted</label>
            <input 
              type="number" 
              className="input"
              value={actualCash} 
              onChange={e => setActualCash(e.target.value)} 
              placeholder="Enter physical cash amount counted"
              style={{ fontSize: 16, fontWeight: 800, height: 44 }}
            />
          </div>

          {calcDifference !== null && (
            <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 10, background: calcDifference === 0 ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', color: calcDifference === 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 800, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{calcDifference === 0 ? '✅ Cash Matched Perfectly!' : calcDifference > 0 ? '⚠️ Excess Cash Found:' : '⚠️ Cash Shortfall:'}</span>
              <strong style={{ fontSize: 15 }}>{fmt(calcDifference)}</strong>
            </div>
          )}

          <div className="field" style={{ marginBottom: 18 }}>
            <label style={{ fontSize: 12 }}>Note / Remarks</label>
            <textarea 
              value={tallyRemark} 
              onChange={e => setTallyRemark(e.target.value)} 
              rows={3} 
              placeholder={hasMoneyMismatch(calcDifference) ? "Required: Please explain the cash difference" : "Optional notes for shift end"} 
              style={{ width: '100%', resize: 'vertical' }} 
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <button className="btn btn-ghost" onClick={() => setConfirmModalOpen(false)}>Cancel</button>
            <button 
              className="btn btn-danger" 
              disabled={actualCash === '' || (calcDifference !== null && hasMoneyMismatch(calcDifference) && !String(tallyRemark || '').trim())} 
              onClick={handleEndShift}
            >
              Confirm End Shift
            </button>
          </div>
        </div>
      </div>
    );
  }

  function renderResultModal() {
    if (!resultModal) return null;
    return (
      <div className="modal-overlay" style={{ zIndex: 10000 }}>
        <div className="modal-content" style={{ maxWidth: 400, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>{resultModal.difference === 0 ? '✅' : '⚠️'}</div>
          <h3>{resultModal.difference === 0 ? 'Shift Closed' : 'Cash Difference Noted'}</h3>
          <div style={{ margin: '20px 0', padding: 16, background: 'var(--s1)', borderRadius: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Actual Cash</span><strong>{fmt(resultModal.closingCash)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Difference</span><strong style={{ color: resultModal.difference === 0 ? 'var(--success)' : 'var(--danger)' }}>{fmt(resultModal.difference)}</strong></div>
          </div>
          <button className="btn btn-gold" style={{ width: '100%' }} onClick={() => setResultModal(null)}>Done</button>
        </div>
      </div>
    );
  }

  function renderEditPaymentModal() {
    if (!editingTx) return null;
    const isNonCash = ['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(editingTx.type);
    return (
      <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', zIndex: 10000 }} onClick={() => setEditingTx(null)}>
        <div className="modal-content" style={{ maxWidth: 440, borderRadius: 20, padding: 32 }} onClick={e => e.stopPropagation()}>
          <h2 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 800 }}>✏️ Edit Payment Record</h2>
          <div className="pay-type-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 12 }}>
            {PAYMENT_TYPES.map(pt => (
              <button key={pt.value} type="button"
                className={`pay-type-btn ${editingTx.type === pt.value ? 'selected' : ''}`}
                onClick={() => setEditingTx(d => ({ ...d, type: pt.value, last5: '' }))}
                style={{ padding: '8px 4px', fontSize: 10 }}>
                <div style={{ fontSize: 16 }}>{pt.icon}</div>
                <div style={{ fontSize: 9, fontWeight: 700, marginTop: 2 }}>{pt.label}</div>
              </button>
            ))}
          </div>
          <div className="form-row-2">
            <div className="field">
              <label>{editingTx.isRefund ? 'Refund Amount (₹) — paid back to guest' : 'Amount (₹)'}</label>
              <input type="number" value={editingTx.amount} onChange={e => setEditingTx(d => ({ ...d, amount: e.target.value }))} />
            </div>
            {isNonCash && (
              <div className="field">
                <label>Reference (Last 5) *</label>
                <input value={editingTx.last5} onChange={e => setEditingTx(d => ({ ...d, last5: e.target.value.slice(-5) }))} maxLength={5} />
              </div>
            )}
          </div>
          <div className="field">
            <label>Remarks</label>
            <input value={editingTx.note} onChange={e => setEditingTx(d => ({ ...d, note: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button className="btn btn-ghost" style={{ flex: 1, border: '1px solid var(--border2)' }} onClick={() => setEditingTx(null)}>Cancel</button>
            <button className="btn btn-gold" style={{ flex: 1.5 }} onClick={async () => {
              if (isNonCash && !editingTx.last5) { await notify('Enter the reference (last 5 digits) for this non-cash payment.'); return; }
              if (!Number.isFinite(Number(editingTx.amount)) || Number(editingTx.amount) <= 0) { await notify('Enter a valid amount.'); return; }
              try {
                // A refund is stored as a negative amount — keep its sign.
                const signedAmount = editingTx.isRefund ? -Math.abs(Number(editingTx.amount)) : Number(editingTx.amount);
                await paymentsApi.update(editingTx.id, { type: editingTx.type, amount: signedAmount, last5: editingTx.last5, note: editingTx.note });
                setEditingTx(null);
                onSuccess?.('Payment record updated.');
                load(); // refresh drawer cash / UPI totals, not just this row
              } catch (err) { await notify(err.message); }
            }}>Save Changes</button>
          </div>
        </div>
      </div>
    );
  }

  if (!hotel) {
    return <div className="empty"><div className="empty-icon">💰</div>Select a property to manage cash</div>;
  }

  if (!isAdmin && !activeShift) {
    return (
      <>
        {renderRecordModal()}
        <div className="panel" style={{ maxWidth: 520, margin: '48px auto', padding: 36 }}>
          <div style={{ fontSize: 34, marginBottom: 12 }}>🕒</div>
          <h2 style={{ marginTop: 0 }}>Start Cash Shift</h2>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18 }}>
            To hand cash to Nagaraju or Govind, use <strong style={{ color: 'var(--text)' }}>Give Money to Owner</strong>.
          </div>
          <div style={{ background: 'var(--s1)', borderRadius: 16, padding: 20, marginBottom: 20, border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', fontWeight: 800 }}>💵 Expected Drawer Cash</div>
              <span className="badge b-gold" style={{ fontSize: 10 }}>Handover Float</span>
            </div>
            <div style={{ fontSize: 32, fontWeight: 900, marginTop: 8, color: 'var(--text)' }}>{fmt(drawerExpectedCash)}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>
              ℹ️ Carried-forward cash that should be physically inside the drawer right now.
            </div>
          </div>

          <div className="field" style={{ marginBottom: 14 }}>
            <label style={{ fontWeight: 700, fontSize: 13 }}>Actual Physical Cash Found</label>
            <input 
              type="number" 
              className="input"
              value={cashInHand} 
              onChange={e => setCashInHand(e.target.value)} 
              placeholder="Enter physical cash amount counted"
              style={{ fontSize: 16, fontWeight: 800, height: 44 }}
            />
          </div>

          {openingDifference !== null && (
            <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 10, background: openingDifference === 0 ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', color: openingDifference === 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 800, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{openingDifference === 0 ? '✅ Cash Matched Perfectly!' : openingDifference > 0 ? '⚠️ Excess Cash Found:' : '⚠️ Cash Shortfall:'}</span>
              <strong style={{ fontSize: 15 }}>{fmt(openingDifference)}</strong>
            </div>
          )}

          <div className="field" style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12 }}>Opening Note / Remarks</label>
            <textarea 
              value={openingRemark} 
              onChange={e => setOpeningRemark(e.target.value)} 
              rows={3} 
              placeholder={hasMoneyMismatch(openingDifference) ? "Required: Please explain the cash difference" : "Optional notes for shift start"} 
              style={{ width: '100%', resize: 'vertical' }} 
            />
          </div>

          <button 
            className="btn btn-gold" 
            style={{ width: '100%', height: 44, fontWeight: 800, fontSize: 15 }} 
            disabled={loading || cashInHand === '' || (openingDifference !== null && hasMoneyMismatch(openingDifference) && !String(openingRemark || '').trim())} 
            onClick={handleJoinShift}
          >
            {loading ? 'Starting Shift...' : '🚀 Start Shift'}
          </button>
          
          <button 
            className="btn btn-danger" 
            style={{ width: '100%', marginTop: 10, padding: 10 }} 
            onClick={openRecordModal}
          >
            👤 Give Money to Owner
          </button>
        </div>
      </>
    );
  }

  // ─── COMPANY DUES (GST company pays after checkout) ───────────────────────
  const newCollectRow = (type = 'cash', amount = '') => ({ id: Date.now() + Math.random(), type, amount: String(amount), last5: '' });
  const COLLECT_TYPES = PAYMENT_TYPES.filter(pt => !['owner_upi', 'owner_credit'].includes(pt.value));
  const NON_CASH = ['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'];

  function timeSince(dateStr) {
    const m = String(dateStr || '').match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
    if (!m) return '';
    const then = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
    const hours = Math.floor((Date.now() - then.getTime()) / 3600000);
    return hours < 1 ? 'just now' : hours < 48 ? `${hours}h ago` : `${Math.floor(hours / 24)} days ago`;
  }

  async function saveCollection() {
    const { due, rows } = collectingDue;
    const entries = rows.filter(r => Number(r.amount) > 0);
    const total = Math.round(entries.reduce((s, r) => s + Number(r.amount), 0) * 100) / 100;
    if (entries.length === 0) { await notify('Enter the amount received.'); return; }
    if (entries.some(r => NON_CASH.includes(r.type) && !String(r.last5).trim())) { await notify('Enter the reference (last 5 digits) for every non-cash payment.'); return; }
    if (total > due.balance + 0.009) { await notify(`Received ${fmt(total)} is more than the ${fmt(due.balance)} still due.`); return; }
    setCollectSaving(true);
    try {
      const updated = await companyDuesApi.collect(due.id, entries.map(r => ({ type: r.type, amount: Number(r.amount), last5: r.last5 })));
      setCollectingDue(null);
      onSuccess?.(updated.balance > 0.009
        ? `${fmt(total)} received from ${due.company}. ${fmt(updated.balance)} still due.`
        : `${fmt(total)} received — ${due.company}'s bill is fully paid.`);
      // TODO(company-dues): receipt printing disabled until utils/receiptPdf.js exists.
      load();
    } catch (err) { await notify(err.message); }
    setCollectSaving(false);
  }

  function renderCollectDueModal() {
    if (!collectingDue) return null;
    const { due, rows } = collectingDue;
    const setRows = (fn) => setCollectingDue(c => ({ ...c, rows: fn(c.rows) }));
    const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const remainingAfter = Math.round((due.balance - total) * 100) / 100;
    return (
      <div className="modal-overlay" style={{ zIndex: 10000 }}>
        <div className="modal-content" style={{ maxWidth: 560 }}>
          <h2 style={{ marginTop: 0 }}>💼 Receive Company Payment</h2>
          <p style={{ color: 'var(--muted)', marginTop: 6, fontSize: 13 }}>
            <strong style={{ color: 'var(--text)' }}>{due.company}</strong> · Invoice {due.invoice_number || '—'} · Room {due.room || '—'}<br />
            Still due: <strong style={{ color: 'var(--danger)' }}>{fmt(due.balance)}</strong> — the company can pay all of it or part of it now.
          </p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setRows(() => [newCollectRow('cash', due.balance)])}>💵 Full Cash</button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setRows(() => [newCollectRow('upi', due.balance)])}>📱 Full UPI</button>
          </div>
          {rows.map((r, idx) => (
            <div key={r.id} style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 700 }}>Payment {idx + 1}</span>
                {rows.length > 1 && <button type="button" onClick={() => setRows(rs => rs.filter(x => x.id !== r.id))} style={{ border: 'none', background: 'none', color: 'var(--danger)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>✕ Remove</button>}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {COLLECT_TYPES.map(pt => (
                  <button key={pt.value} type="button" className={`pay-type-btn ${r.type === pt.value ? 'selected' : ''}`} style={{ padding: '6px 8px', fontSize: 10 }}
                    onClick={() => setRows(rs => rs.map(x => x.id === r.id ? { ...x, type: pt.value, last5: '' } : x))}>
                    {pt.icon} {pt.label}
                  </button>
                ))}
              </div>
              <div className="form-row-2">
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Amount (₹)</label>
                  <input type="number" min="0" value={r.amount} onChange={e => setRows(rs => rs.map(x => x.id === r.id ? { ...x, amount: e.target.value } : x))} />
                </div>
                {NON_CASH.includes(r.type) && (
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Reference (Last 5) *</label>
                    <input value={r.last5} maxLength={5} onChange={e => setRows(rs => rs.map(x => x.id === r.id ? { ...x, last5: e.target.value.replace(/\D/g, '').slice(0, 5) } : x))} />
                  </div>
                )}
              </div>
            </div>
          ))}
          {remainingAfter > 0.5 && (
            <button type="button" onClick={() => setRows(rs => [...rs, newCollectRow('upi', remainingAfter)])}
              style={{ width: '100%', padding: 8, border: '2px dashed var(--border)', borderRadius: 8, background: 'transparent', color: 'var(--accent)', fontWeight: 700, fontSize: 12, cursor: 'pointer', marginBottom: 8 }}>
              + Add Another Payment Method (half cash / half UPI)
            </button>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 800, padding: '8px 12px', borderRadius: 8, background: remainingAfter < -0.009 ? 'rgba(239,68,68,0.1)' : 'var(--s1)' }}>
            <span>Receiving {fmt(total)}</span>
            <span style={{ color: remainingAfter < -0.009 ? 'var(--danger)' : remainingAfter > 0.009 ? 'var(--gold)' : 'var(--success)' }}>
              {remainingAfter < -0.009 ? `${fmt(-remainingAfter)} more than due` : remainingAfter > 0.009 ? `${fmt(remainingAfter)} will remain due` : '✅ Clears the due'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button className="btn btn-ghost" style={{ flex: 1, border: '1px solid var(--border2)' }} onClick={() => setCollectingDue(null)} disabled={collectSaving}>Cancel</button>
            <button className="btn btn-gold" style={{ flex: 1.5 }} onClick={saveCollection} disabled={collectSaving || total <= 0 || remainingAfter < -0.009}>
              {collectSaving ? 'Saving…' : 'Record Payment'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  function renderCompanyDues() {
    if (companyDues.length === 0) return null;
    const totalDue = companyDues.reduce((s, d) => s + Number(d.balance || 0), 0);
    const overdue = companyDues.filter(d => d.is_overdue).length;
    return (
      <div className="panel" style={{ marginBottom: 24, border: overdue > 0 ? '1.5px solid var(--danger)' : undefined }}>
        <div className="panel-head">
          <span className="panel-title">💼 Company Dues ({companyDues.length}){overdue > 0 && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--danger)' }}>⚠️ {overdue} overdue</span>}</span>
          <span className="panel-sub">{fmt(totalDue)} to be collected from companies — overdue 24 hours after checkout</span>
        </div>
        <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {companyDues.map(d => (
            <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: d.is_overdue ? 'rgba(239,68,68,0.06)' : 'var(--s1)', border: `1px solid ${d.is_overdue ? 'rgba(239,68,68,0.35)' : 'var(--border)'}`, borderRadius: 8, padding: '10px 14px' }}>
              <div style={{ minWidth: 220 }}>
                <div style={{ fontWeight: 800, fontSize: 13 }}>
                  {d.company} {d.is_overdue && <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 5, background: 'var(--danger)', color: 'white', marginLeft: 6 }}>OVERDUE</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                  Inv {d.invoice_number || '—'} · Room {d.room || '—'} · {d.guest_name || 'Guest'} · checked out {timeSince(d.created_at)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                  📞 {d.contact_name} {d.contact_phone}{d.promised_note ? ` · "${d.promised_note}"` : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 900, fontSize: 16, color: 'var(--danger)' }}>{fmt(d.balance)}</div>
                {d.collected > 0 && <div style={{ fontSize: 10, color: 'var(--muted)' }}>{fmt(d.collected)} of {fmt(d.amount)} received</div>}
                <button className="btn btn-sm btn-gold" style={{ marginTop: 6 }} onClick={() => setCollectingDue({ due: d, rows: [newCollectRow('cash', '')] })}>Receive Payment</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  function renderPendingOwnerCredits() {
    if (pendingOwnerCredits.length === 0) return null;
    const canSettle = true;
    const totalPending = pendingOwnerCredits.reduce((s, c) => s + Number(c.amount), 0);
    return (
      <div className="panel" style={{ marginBottom: 24 }}>
        <div className="panel-head">
          <span className="panel-title">🤝 Pending Owner Settlements ({pendingOwnerCredits.length})</span>
          <span className="panel-sub">{fmt(totalPending)} owed to the owner directly — not yet collected</span>
        </div>
        <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {pendingOwnerCredits.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{c.guest_name || 'Guest'} {c.room ? `· Room ${c.room}` : ''}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                  {new Date(c.timestamp || c.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  {c.note ? ` · ${c.note}` : ''} · recorded by {c.recorded_by_name}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--danger)' }}>{fmt(c.amount)}</span>
                {canSettle && (
                  <button className="btn btn-sm btn-gold" onClick={() => setSettlingCredit({ id: c.id, amount: Number(c.amount), guest_name: c.guest_name, room: c.room, settledAmount: String(c.amount) })}>
                    ✅ Mark as Settled
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  function renderSettleCreditModal() {
    if (!settlingCredit) return null;
    const paid = Number(settlingCredit.settledAmount) || 0;
    const discount = Math.max(0, settlingCredit.amount - paid);
    return (
      <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', zIndex: 10000 }} onClick={() => setSettlingCredit(null)}>
        <div className="modal-content" style={{ maxWidth: 420, borderRadius: 20, padding: 32 }} onClick={e => e.stopPropagation()}>
          <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 800 }}>✅ Settle Owner Credit</h2>
          <p style={{ margin: '0 0 20px', color: 'var(--muted)', fontSize: 13 }}>
            {settlingCredit.guest_name || 'Guest'} {settlingCredit.room ? `· Room ${settlingCredit.room}` : ''} — originally owed {fmt(settlingCredit.amount)}
          </p>
          <div className="field">
            <label>Amount Actually Paid to Owner (₹)</label>
            <input type="number" min="0" max={settlingCredit.amount} value={settlingCredit.settledAmount} onChange={e => setSettlingCredit(s => ({ ...s, settledAmount: e.target.value }))} />
          </div>
          <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8, background: discount > 0 ? 'rgba(232,184,109,0.10)' : 'rgba(109,191,148,0.12)', fontSize: 13, fontWeight: 600 }}>
            {discount > 0 ? `Discount given by owner: ${fmt(discount)}` : '✅ Paid in full, no discount'}
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
            <button className="btn btn-ghost" style={{ flex: 1, border: '1px solid var(--border2)' }} onClick={() => setSettlingCredit(null)}>Cancel</button>
            <button className="btn btn-gold" style={{ flex: 1.5 }} onClick={async () => {
              if (!Number.isFinite(paid) || paid < 0) { await notify('Enter a valid amount.'); return; }
              if (paid > settlingCredit.amount) { await notify(`Amount paid can't exceed the original ${fmt(settlingCredit.amount)} owed.`); return; }
              try {
                await paymentsApi.settle(settlingCredit.id, { settledAmount: paid });
                setPendingOwnerCredits(prev => prev.filter(c => c.id !== settlingCredit.id));
                setSettlingCredit(null);
                onSuccess?.('Owner credit settled.');
              } catch (err) { await notify(err.message); }
            }}>Confirm Settlement</button>
          </div>
        </div>
      </div>
    );
  }

  function renderTransactionAuditLog() {
    const filtered = transactions.filter(t => {
      if (txCategoryFilter !== 'all' && t.category !== txCategoryFilter) return false;
      if (txMethodFilter !== 'all') {
        const m = (t.method || '').toLowerCase();
        if (txMethodFilter === 'cash' && m !== 'cash') return false;
        if (txMethodFilter === 'upi' && !['upi', 'online'].includes(m)) return false;
        if (txMethodFilter === 'card' && !['credit_card', 'debit_card', 'card'].includes(m)) return false;
        if (txMethodFilter === 'cheque' && m !== 'cheque' && m !== 'bank_transfer') return false;
      }
      if (txDate) {
        const tDateStr = new Date(t.timestamp).toISOString().slice(0, 10);
        if (tDateStr !== txDate) return false;
      }
      if (txSearch.trim()) {
        const q = txSearch.toLowerCase();
        const guest = (t.guest_name || '').toLowerCase();
        const phone = (t.guest_phone || '').toLowerCase();
        const room = (t.room || '').toLowerCase();
        const note = (t.note || '').toLowerCase();
        const purpose = (t.purpose || '').toLowerCase();
        const staff = (t.recorded_by_name || '').toLowerCase();
        if (!guest.includes(q) && !phone.includes(q) && !room.includes(q) && !note.includes(q) && !purpose.includes(q) && !staff.includes(q)) {
          return false;
        }
      }
      return true;
    });

    const totalCashSum = filtered.filter(t => (t.method || '').toLowerCase() === 'cash' && t.category === 'payment').reduce((s, t) => s + Number(t.amount || 0), 0);
    const totalOnlineSum = filtered.filter(t => ['upi', 'online', 'credit_card', 'debit_card', 'card'].includes((t.method || '').toLowerCase()) && t.category === 'payment').reduce((s, t) => s + Number(t.amount || 0), 0);
    const totalExpSum = filtered.filter(t => t.category === 'expense').reduce((s, t) => s + Number(t.amount || 0), 0);
    const totalHandoverSum = filtered.filter(t => t.category === 'owner_handover').reduce((s, t) => s + Number(t.amount || 0), 0);
    const totalTransferSum = filtered.filter(t => t.category === 'cash_transfer').reduce((s, t) => s + Number(t.amount || 0), 0);

    return (
      <div className="panel" style={{ borderRadius: 22, marginTop: 24, overflow: 'hidden' }}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
              🔍 Shift Financial Audit Log & All Transactions
              {user?.role === 'staff' ? (
                <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 999, background: 'rgba(239,68,68,0.12)', color: 'var(--danger)' }}>
                  🔒 My Data Only
                </span>
              ) : (
                <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 999, background: 'rgba(59,130,246,0.12)', color: '#2563eb' }}>
                  🌐 Super Admin / Manager Full Access
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
              Complete record of room payments, online collections, expenses, and owner handovers
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ background: 'rgba(34,197,94,0.1)', padding: '6px 12px', borderRadius: 10, fontSize: 12, fontWeight: 800, color: 'var(--success)' }}>
              Cash Rec: {fmt(totalCashSum)}
            </div>
            <div style={{ background: 'rgba(20,184,166,0.1)', padding: '6px 12px', borderRadius: 10, fontSize: 12, fontWeight: 800, color: 'var(--teal)' }}>
              Online Rec: {fmt(totalOnlineSum)}
            </div>
            <div style={{ background: 'rgba(239,68,68,0.1)', padding: '6px 12px', borderRadius: 10, fontSize: 12, fontWeight: 800, color: 'var(--danger)' }}>
              Expenses: {fmt(totalExpSum)}
            </div>
            <div style={{ background: 'rgba(180,83,9,0.1)', padding: '6px 12px', borderRadius: 10, fontSize: 12, fontWeight: 800, color: '#b45309' }}>
              Handed Out: {fmt(totalHandoverSum)}
            </div>
            {totalTransferSum > 0 && (
              <div style={{ background: 'rgba(37,99,235,0.1)', padding: '6px 12px', borderRadius: 10, fontSize: 12, fontWeight: 800, color: '#1d4ed8' }} title="UPI received and the same cash handed out — not revenue">
                Cash Transfers: {fmt(totalTransferSum)}
              </div>
            )}
          </div>
        </div>

        {/* Filter / Search Bar */}
        <div style={{ padding: '14px 20px', background: 'var(--s1)', borderBottom: '1px solid var(--border)', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: '1 1 200px' }}>
            <input 
              className="input" 
              placeholder="🔎 Search by Guest, Room, Phone, Staff or Purpose..."
              value={txSearch}
              onChange={e => setTxSearch(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 13 }}
            />
          </div>
          <input 
            type="date"
            className="input"
            value={txDate}
            onChange={e => setTxDate(e.target.value)}
            style={{ width: 145, height: 38, fontSize: 13 }}
            title="Filter transactions by date"
          />
          <select className="input" value={txCategoryFilter} onChange={e => setTxCategoryFilter(e.target.value)} style={{ width: 160, height: 38, fontSize: 13 }}>
            <option value="all">All Categories</option>
            <option value="payment">💰 Payments Received</option>
            <option value="expense">💸 Expenses</option>
            <option value="owner_handover">👤 Owner Handover</option>
            <option value="cash_transfer">⇄ Cash Transfers</option>
          </select>
          <select className="input" value={txMethodFilter} onChange={e => setTxMethodFilter(e.target.value)} style={{ width: 160, height: 38, fontSize: 13 }}>
            <option value="all">All Payment Methods</option>
            <option value="cash">💵 Cash</option>
            <option value="upi">📱 UPI / Online</option>
            <option value="card">💳 Card</option>
            <option value="cheque">📜 Cheque / Bank</option>
          </select>
          {(txSearch || txDate || txCategoryFilter !== 'all' || txMethodFilter !== 'all') && (
            <button className="btn btn-sm btn-ghost" onClick={() => { setTxSearch(''); setTxDate(''); setTxCategoryFilter('all'); setTxMethodFilter('all'); }}>Clear</button>
          )}
        </div>

        {/* Transactions Table */}
        <div className="tbl-wrap" style={{ maxHeight: 420, overflowY: 'auto' }}>
          <table className="tbl-sm" style={{ width: '100%' }}>
            <thead>
              <tr style={{ background: 'var(--s2)', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '10px 12px', fontSize: 11 }}>Date & Time</th>
                <th style={{ padding: '10px 12px', fontSize: 11 }}>Category</th>
                <th style={{ padding: '10px 12px', fontSize: 11 }}>Method</th>
                <th style={{ padding: '10px 12px', fontSize: 11 }}>Guest / Description</th>
                <th style={{ padding: '10px 12px', fontSize: 11 }}>Recorded By</th>
                <th style={{ padding: '10px 12px', fontSize: 11, textAlign: 'right' }}>Amount</th>
                {user?.role === 'superadmin' && <th style={{ padding: '10px 12px', fontSize: 11 }}></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={user?.role === 'superadmin' ? 7 : 6} style={{ textAlign: 'center', padding: '32px 0', color: 'var(--muted)', fontSize: 13 }}>
                    No matching transactions found.
                  </td>
                </tr>
              ) : filtered.map(t => {
                const isPayment = t.category === 'payment';
                const isExpense = t.category === 'expense';
                const isTransfer = t.category === 'cash_transfer';

                const catBadge = isPayment ? { label: 'Payment', bg: 'rgba(34,197,94,0.12)', color: 'var(--success)' }
                  : isExpense ? { label: 'Expense', bg: 'rgba(239,68,68,0.12)', color: 'var(--danger)' }
                  : isTransfer ? { label: 'Cash Transfer', bg: 'rgba(37,99,235,0.12)', color: '#1d4ed8' }
                  : { label: 'Owner Handover', bg: 'rgba(180,83,9,0.12)', color: '#b45309' };

                const mName = (t.method || 'cash').toLowerCase();
                const methodBadge = isTransfer ? `📱 UPI → 💵 Cash${t.last5 ? ` (${t.last5})` : ''}`
                  : mName === 'cash' ? '💵 Cash'
                  : ['upi', 'online'].includes(mName) ? '📱 UPI / Online'
                  : ['credit_card', 'debit_card', 'card'].includes(mName) ? '💳 Card'
                  : mName === 'owner_upi' ? '📲 Owner UPI'
                  : mName === 'owner_credit' ? (t.owner_settlement_status === 'pending' ? '🤝 Owner Credit (Pending)' : '🤝 Owner Credit (Settled)')
                  : '📜 ' + (t.method || 'Other');

                return (
                  <tr key={t.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>
                        {new Date(t.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                        {new Date(t.timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 6, background: catBadge.bg, color: catBadge.color }}>
                        {catBadge.label}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 11, fontWeight: 700 }}>
                      {methodBadge}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontSize: 12, fontWeight: 800 }}>
                        {isTransfer ? 'Cash given for UPI (not billed)' : (t.guest_name || t.purpose || '—')} {t.room ? `(Room ${t.room})` : ''}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>
                        {t.note || t.guest_phone || '—'}
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 11, color: 'var(--muted)' }}>
                      {t.recorded_by_name || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 900, fontSize: 14, color: isTransfer ? '#1d4ed8' : isPayment ? 'var(--success)' : isExpense ? 'var(--danger)' : '#b45309' }}>
                      {isTransfer ? '⇄ ' : isPayment ? '+' : '-'}{fmt(t.amount)}
                    </td>
                    {user?.role === 'superadmin' && (
                      <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {isTransfer && (
                          <button
                            title="Delete this cash transfer (both the UPI and cash entries)"
                            onClick={async () => {
                              const ok = await confirm({
                                icon: '🗑️', title: 'Delete Cash Transfer', danger: true, confirmText: 'Delete',
                                body: `Delete this ${fmt(t.amount)} cash transfer? Both the UPI entry and the cash entry are removed.`
                              });
                              if (!ok) return;
                              try {
                                await cashTransfersApi.delete(t.id);
                                onSuccess?.('Cash transfer deleted.');
                                load();
                              } catch (err) { await notify(err.message); }
                            }}
                            style={{ border: 'none', background: 'rgba(239,68,68,0.08)', color: 'var(--danger)', cursor: 'pointer', width: 26, height: 26, borderRadius: 6, fontSize: 12 }}
                          >🗑️</button>
                        )}
                        {isPayment && (
                          <>
                            <button
                              title="Edit this payment record"
                              onClick={() => setEditingTx({ id: t.id, type: t.method || 'cash', amount: Math.abs(t.amount), isRefund: Number(t.amount) < 0, last5: t.last5 || '', note: t.note || '' })}
                              style={{ border: 'none', background: 'rgba(59,130,246,0.08)', color: '#3b82f6', cursor: 'pointer', width: 26, height: 26, borderRadius: 6, fontSize: 12, marginRight: 6 }}
                            >✏️</button>
                            <button
                              title="Delete this payment record"
                              onClick={async () => {
                                const ok = await confirm({
                                  icon: '🗑️', title: 'Delete Payment Record', danger: true, confirmText: 'Delete',
                                  body: `Permanently delete this ${fmt(Math.abs(t.amount))} ${methodBadge} entry for ${t.guest_name || 'this guest'}? This cannot be undone.`
                                });
                                if (!ok) return;
                                try {
                                  await paymentsApi.delete(t.id);
                                  onSuccess?.('Payment record deleted.');
                                  load(); // refresh drawer cash / UPI totals
                                } catch (err) { await notify(err.message); }
                              }}
                              style={{ border: 'none', background: 'rgba(239,68,68,0.08)', color: 'var(--danger)', cursor: 'pointer', width: 26, height: 26, borderRadius: 6, fontSize: 12 }}
                            >🗑️</button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-in">
      {renderRecordModal()}
      {renderTransferModal()}
      {renderCollectDueModal()}
      {renderConfirmModal()}
      {renderResultModal()}
      {renderEditPaymentModal()}
      {renderSettleCreditModal()}

      <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontWeight: 900, fontSize: 34 }}>💎 Cash Management</h1>
          <p style={{ margin: '8px 0 0', color: 'var(--muted)' }}>
          {isAdmin ? 'Owner-friendly monthly financial view' : 'Shift cash tracking and owner handovers'} for {hotel.name}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {isAdmin && <input type="month" className="input" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} style={{ width: 170 }} />}
          <button className="btn btn-gold" onClick={load}>Refresh</button>
          {(isAdmin || activeShift) && (
            <button className="btn btn-ghost" style={{ border: '1px solid #1d4ed8', color: '#1d4ed8', fontWeight: 800 }} onClick={openTransferModal}>⇄ Cash Transfer</button>
          )}
          <button className="btn btn-danger" onClick={openRecordModal}>{isAdmin ? 'Record Money Given' : 'Give Money to Owner'}</button>
          {!isAdmin && activeShift && (
            <button className="btn btn-ghost" onClick={() => { setActualCash(''); setTallyRemark(''); setConfirmModalOpen(true); }}>
              End Shift
            </button>
          )}
        </div>
      </div>

      {!isAdmin && activeShift && (
        <div style={{ marginBottom: 24 }}>
          <div className="panel" style={{ padding: 18, borderRadius: 22, marginBottom: 16, border: '1px solid rgba(180, 83, 9, 0.2)', background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(255,255,255,0.96))' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase' }}>Staff Quick Action</div>
                <div style={{ fontSize: 22, fontWeight: 900, marginTop: 6 }}>Need to hand cash to Nagaraju or Govind?</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>
                  Use <strong style={{ color: 'var(--text)' }}>Give Money to Owner</strong> to enter the amount, person, and note.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button className="btn btn-ghost" style={{ border: '1px solid #1d4ed8', color: '#1d4ed8', fontWeight: 800 }} onClick={openTransferModal}>⇄ Cash Transfer (UPI → Cash)</button>
                <button className="btn btn-danger" onClick={openRecordModal}>Give Money to Owner</button>
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14 }}>
            {(() => {
              const openingCash = Number(activeShift?.opening_cash || 0);
              const netImpact = shiftStats.userCash - shiftStats.userCashExpenses - shiftStats.userOwnerPickups;
              const expectedDrawerCash = openingCash + netImpact;
              return [
                { label: 'Opening Cash', value: fmt(openingCash), color: '#4b5563' },
                { label: 'Shift Cash', value: fmt(shiftStats.userCash), color: 'var(--success)' },
                { label: 'Shift Online', value: fmt(shiftStats.userOnline), color: 'var(--teal)' },
                { label: 'Cash Expenses', value: fmt(shiftStats.userCashExpenses), color: 'var(--danger)' },
                { label: 'Handed to Owner', value: fmt(shiftStats.userOwnerPickups), color: '#b45309' },
                { label: 'Net Shift Cash', value: fmt(netImpact), color: 'var(--accent)' },
                { label: 'Expected Drawer Cash', value: fmt(expectedDrawerCash), color: '#1d4ed8', highlight: true },
              ].map((item) => (
                <div key={item.label} className="panel" style={{
                  padding: 14,
                  border: item.highlight ? '2px solid #2563eb' : '1px solid var(--border)',
                  background: item.highlight ? 'rgba(37,99,235,0.06)' : 'var(--panel)',
                }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: item.highlight ? '#1d4ed8' : 'var(--muted)', textTransform: 'uppercase' }}>{item.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: item.color, marginTop: 6 }}>{item.value}</div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {isAdmin && renderOverviewStrip()}
      {isAdmin && renderHotelBreakdowns()}
      <GuestTasksWidget hotel={hotel} user={user} />
      {renderCompanyDues()}
      {renderPendingOwnerCredits()}
      {renderTransactionAuditLog()}
      {isAdmin && renderMoneyRegister()}
      {!isAdmin && renderStaffPickupHistory()}
    </div>
  );
}
